import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop, hexToRGBA, getStrokeDasharray, isDefined } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import { XCoordinate } from "../../coordinates";

class EachVerticalLine extends Component {
  constructor(props) {
    super(props);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleHover = this.handleHover.bind(this);
    this.isHover = this.isHover.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.renderSVG = this.renderSVG.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleDragStart() {
    const { xValue } = this.props;
    this.dragStart = {
      xValue,
    };
  }

  handleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const { xValue } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x = xScale(xValue);

    const dx = startPos[0] - mouseXY[0];

    const newXValue = getXValue(
      xScale,
      xAccessor,
      [x - dx, yScale.invert(mouseXY[1])],
      fullData,
    );

    onDrag(index, {
      xValue: newXValue,
    });
  }

  handleDragComplete(...rest) {
    this.dragStart = null;
    this.props.onDragComplete(...rest);
  }

  isHover(moreProps) {
    const { xValue, tolerance } = this.props;
    const { mouseXY, xScale } = moreProps;

    const lineX = xScale(xValue);
    const [mouseX] = mouseXY;

    return Math.abs(mouseX - lineX) <= (tolerance || 7);
  }

  drawOnCanvas(ctx, moreProps) {
    const { xValue, stroke, strokeWidth, strokeOpacity, strokeDasharray } =
      this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x = xScale(xValue);

    const range = yScale.range();
    const yStart = range[range.length - 1];
    const yEnd = range[0];

    ctx.save();
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);

    const dashSegments = getStrokeDasharray(strokeDasharray);
    if (dashSegments !== "none") {
      ctx.setLineDash(dashSegments.split(",").map((d) => Number(d.trim())));
    } else {
      ctx.setLineDash([]);
    }

    ctx.beginPath();
    ctx.moveTo(x, yStart);
    ctx.lineTo(x, yEnd);
    ctx.stroke();
    ctx.restore();
  }

  renderSVG(moreProps) {
    const { xValue, stroke, strokeWidth, strokeOpacity, strokeDasharray } =
      this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x = xScale(xValue);
    const range = yScale.range();
    const yStart = range[range.length - 1];
    const yEnd = range[0];

    return (
      <line
        x1={x}
        y1={yStart}
        x2={x}
        y2={yEnd}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        strokeDasharray={getStrokeDasharray(strokeDasharray)}
      />
    );
  }

  render() {
    const {
      selected,
      hoverText,
      interactive,
      interactiveCursorClass,
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      displayFormat,
      coordinate,
      showCoordinate,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;

    const cursorClass =
      interactiveCursorClass || "react-stockcharts-move-cursor";

    return (
      <g>
        <GenericChartComponent
          isHover={interactive !== false ? this.isHover : null}
          onHover={interactive !== false ? this.handleHover : null}
          onUnHover={interactive !== false ? this.handleHover : null}
          onDragStart={interactive !== false ? this.handleDragStart : null}
          onDrag={interactive !== false ? this.handleDrag : null}
          onDragComplete={
            interactive !== false ? this.handleDragComplete : null
          }
          svgDraw={this.renderSVG}
          canvasDraw={this.drawOnCanvas}
          canvasToDraw={getMouseCanvas}
          interactiveCursorClass={
            interactive !== false ? cursorClass : undefined
          }
          selected={selected}
          enableDragOnHover={interactive !== false}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />
        {showCoordinate !== false &&
          interactive !== false &&
          isDefined(displayFormat) && (
            <XCoordinate
              xValue={this.props.xValue}
              displayFormat={displayFormat}
              orient={coordinate?.orient || "bottom"}
              at={coordinate?.at || "bottom"}
              rectWidth={coordinate?.rectWidth || 80}
              rectHeight={coordinate?.rectHeight || 20}
              arrowWidth={coordinate?.arrowWidth || 0}
              dy={coordinate?.dy || 0}
              fill={coordinate?.fill || stroke}
              opacity={isDefined(coordinate?.opacity) ? coordinate.opacity : 1}
              textFill={coordinate?.textFill || "#FFFFFF"}
              fontFamily={
                coordinate?.fontFamily ||
                "Helvetica Neue, Helvetica, Arial, sans-serif"
              }
              fontSize={coordinate?.fontSize || 12}
              fontWeight={coordinate?.fontWeight || "normal"}
              stroke={coordinate?.stroke || stroke}
              strokeOpacity={
                isDefined(coordinate?.strokeOpacity)
                  ? coordinate.strokeOpacity
                  : strokeOpacity
              }
              strokeWidth={coordinate?.strokeWidth || strokeWidth}
              lineOpacity={coordinate?.lineOpacity || 0}
              lineStroke={coordinate?.lineStroke || stroke}
            />
          )}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && interactive !== false}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

EachVerticalLine.propTypes = {
  index: PropTypes.number,
  xValue: PropTypes.any.isRequired,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeOpacity: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  selected: PropTypes.bool,
  hoverText: PropTypes.object.isRequired,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  interactive: PropTypes.bool,
  interactiveCursorClass: PropTypes.string,
  tolerance: PropTypes.number,
  coordinate: PropTypes.object,
  displayFormat: PropTypes.func,
  showCoordinate: PropTypes.bool,
};

EachVerticalLine.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  interactive: true,
  selected: false,
  hoverText: {
    enable: false,
  },
  strokeDasharray: "Solid",
  strokeOpacity: 1,
  strokeWidth: 1,
  tolerance: 7,
  coordinate: {},
  showCoordinate: true,
  displayFormat: (d) => (d instanceof Date ? d.toLocaleString() : String(d)),
};

export default EachVerticalLine;
